import * as React from 'react';
import { useQueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { groupChatService } from '@/services/groupChatService';
import type { GroupMessageData, GroupHistoryResponse } from '@/services/groupChatService';
import { MessageSender, type Message } from '@/types';
import type { SSEEvent } from '@/services/chatService';
import { useToast } from '@/context/ToastContext';
import { useUiStore } from '@/store/uiStore';
import { useAuth } from '@/hooks/useAuth';
import { billingRecoveryContext } from '@/utils/billingRecovery';

interface GroupMessage extends Message {
  echo_id?: string | null;
  audioBuffer?: string[];
  isAudioReady?: boolean;
  isTextComplete?: boolean;
  isAllAudioStreamed?: boolean;
  ttsFailed?: boolean;
}

interface UseGroupChatMessagesOptions {
  groupId: string;
  ttsProvider: 'azure' | 'elevenlabs';
  generateAudio: boolean;
}

interface UseGroupChatMessagesReturn {
  messages: GroupMessage[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  total: number;
  sendMessage: (content: string) => Promise<void>;
  sendAudioMessage: (blob: Blob, waveform: number[], duration: number) => Promise<void>;
  clearConversation: () => Promise<void>;
  loadMore: () => void;
  isSending: boolean;
  typingEchoName: string | null;
}

const PAGE_SIZE = 20;

function mapServerMessage(msg: GroupMessageData): GroupMessage {
  return {
    id: msg.id,
    content: msg.content,
    sender_type: msg.sender_type === 'user' ? MessageSender.USER : MessageSender.AI,
    created_at: msg.created_at,
    echo_id: msg.echo_id,
    ...(msg.audio_url ? { audio_url: msg.audio_url } : {}),
    ...(msg.audio_url_azure ? { audio_url_azure: msg.audio_url_azure } : {}),
    ...(msg.audio_url_elevenlabs ? { audio_url_elevenlabs: msg.audio_url_elevenlabs } : {}),
    suggested_questions: msg.suggested_questions,
    is_read: msg.is_read,
    isAudioReady: !!(msg.audio_url || msg.audio_url_azure || msg.audio_url_elevenlabs),
    isTextComplete: true,
    isAllAudioStreamed: true,
    ...(msg.rating ? { rating: msg.rating } : {}),
  };
}

/**
 * Group-chat counterpart to `useChat` — manages SSE-streamed AI replies for a 2-echo group,
 * paginated history, optimistic sends, audio uploads, and clear-conversation. Returns the
 * shape `ChatView` expects via its `groupChat` prop.
 */
export function useGroupChatMessages({
  groupId,
  ttsProvider,
  generateAudio,
}: UseGroupChatMessagesOptions): UseGroupChatMessagesReturn {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const openBillingRecovery = useUiStore((state) => state.openBillingRecovery);
  const { refreshProfile } = useAuth();

  // Session messages: user messages + finalized AI messages stay here permanently.
  // No refetch needed — dedup against history handles remounts.
  const [activeMessages, setActiveMessages] = React.useState<GroupMessage[]>([]);
  const [isSending, setIsSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [typingEchoName, setTypingEchoName] = React.useState<string | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const totalCountRef = React.useRef<number>(0);

  const queryKey = React.useMemo(() => ['group-chat-history', groupId], [groupId]);

  // Meta query: fetch total count so we can start pagination from the newest page
  const { data: metaData, isFetched: isMetaFetched } = useQuery({
    queryKey: ['group-chat-meta', groupId],
    queryFn: async () => {
      const result = await groupChatService.getHistory(groupId, 0, 1);
      totalCountRef.current = result.total;
      return result;
    },
    enabled: !!groupId,
    staleTime: 30_000,
    refetchOnMount: true,
  });

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      let currentOffset = pageParam as number;
      // Sentinel -1 → start from the newest page
      if (currentOffset === -1) {
        const total = totalCountRef.current;
        currentOffset = Math.max(0, total - PAGE_SIZE);
      }
      const res = await groupChatService.getHistory(groupId, currentOffset, PAGE_SIZE);
      return { ...res, pageParam: currentOffset };
    },
    initialPageParam: -1,
    getNextPageParam: (lastPage: GroupHistoryResponse & { pageParam: number }) => {
      // "Load more" goes backward (older messages)
      const currentOffset = lastPage.pageParam ?? 0;
      if (currentOffset === 0) return undefined;
      const nextOffset = Math.max(0, currentOffset - PAGE_SIZE);
      return nextOffset === currentOffset ? undefined : nextOffset;
    },
    enabled: !!groupId && isMetaFetched,
    staleTime: Infinity, // Never auto-refetch; we manage state locally
  });

  const historyMessages = React.useMemo<GroupMessage[]>(() => {
    if (!data?.pages) return [];
    const all: GroupMessage[] = [];
    for (const page of data.pages) {
      for (const msg of (page as GroupHistoryResponse).messages) {
        all.push(mapServerMessage(msg));
      }
    }
    // Sort chronologically (oldest → newest) since pages may arrive in any order
    all.sort(
      (left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime()
    );
    return all;
  }, [data]);

  // Merge history + session messages, dedup by ID
  const messages = React.useMemo<GroupMessage[]>(() => {
    const seen = new Set<string>();
    const combined: GroupMessage[] = [];
    // History first (oldest → newest)
    for (const msg of historyMessages) {
      if (!seen.has(msg.id)) {
        seen.add(msg.id);
        combined.push(msg);
      }
    }
    // Then active/session messages (user msgs, streaming AI, finalized AI)
    for (const msg of activeMessages) {
      if (!seen.has(msg.id)) {
        seen.add(msg.id);
        combined.push(msg);
      }
    }
    return combined;
  }, [historyMessages, activeMessages]);

  const total = metaData?.total ?? 0;

  // Refetch history on mount only if the cache has existing data (avoids redundant fetch on first load)
  React.useEffect(() => {
    const existing = queryClient.getQueryData(queryKey);
    if (existing) {
      queryClient.invalidateQueries({ queryKey });
    }
  }, [queryClient, queryKey]);

  // Cleanup on unmount
  React.useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  // Reset on group change
  React.useEffect(() => {
    setActiveMessages([]);
    setError(null);
    setTypingEchoName(null);
    abortRef.current?.abort();
  }, [groupId]);

  /**
   * Shared SSE streaming logic used by both sendMessage and sendAudioMessage.
   * Handles echo responses, text chunks, audio chunks, and state updates.
   */
  const _streamResponse = React.useCallback(
    async (content: string, placeholderId: string) => {
      let currentEchoId: string | null = null;
      let currentEchoName: string | null = null;
      let accumulatedText = '';
      let currentTempId: string | null = null;
      const audioChunks: string[] = [];
      let respondingEchoIds: string[] = [];
      const completedEchoIds: string[] = [];

      const controller = new AbortController();
      abortRef.current = controller;

      const handleGroupStart = (payload: Record<string, unknown>) => {
        respondingEchoIds = (payload['responding_echo_ids'] as string[]) ?? [];
        if (respondingEchoIds.length > 0) {
          setActiveMessages((prev) =>
            prev.map((msg) =>
              msg.id === placeholderId ? { ...msg, echo_id: respondingEchoIds[0] ?? null } : msg
            )
          );
        }
      };

      const handleEchoStart = (payload: Record<string, unknown>) => {
        currentEchoId = payload['echo_id'] as string;
        currentEchoName = payload['echo_name'] as string;
        accumulatedText = '';
        audioChunks.length = 0;
        currentTempId = `temp-ai-${currentEchoId}-${Date.now()}`;
        setTypingEchoName(currentEchoName);

        const echoPlaceholder: GroupMessage = {
          id: currentTempId,
          content: '',
          sender_type: MessageSender.AI,
          created_at: new Date().toISOString(),
          echo_id: currentEchoId,
          isTextComplete: false,
          isAudioReady: false,
        };
        setActiveMessages((prev) => [
          ...prev.filter((msg) => msg.id !== placeholderId),
          echoPlaceholder,
        ]);
      };

      const handleTextChunk = (payload: Record<string, unknown>) => {
        accumulatedText += (payload['content'] as string) || '';
        const tempId = currentTempId;
        if (tempId) {
          setActiveMessages((prev) =>
            prev.map((msg) => (msg.id === tempId ? { ...msg, content: accumulatedText } : msg))
          );
        }
      };

      const handleAllAudioComplete = () => {
        const tempId = currentTempId;
        if (!tempId) return;
        const buffer = audioChunks.length > 0 ? [...audioChunks] : undefined;
        setActiveMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? {
                  ...msg,
                  ...(buffer ? { audioBuffer: buffer } : {}),
                  isAudioReady: !!buffer,
                  isAllAudioStreamed: true,
                  isTextComplete: true,
                }
              : msg
          )
        );
      };

      const handleAudioError = () => {
        const tempId = currentTempId;
        if (!tempId) return;
        setActiveMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? { ...msg, ttsFailed: true, isTextComplete: true, isAllAudioStreamed: true }
              : msg
          )
        );
      };

      const handleEchoEnd = (payload: Record<string, unknown>) => {
        const msgId = payload['message_id'] as string;
        const finalContent = (payload['content'] as string) || accumulatedText;
        const suggestions = payload['suggested_questions'] as
          | Array<{ id: string; text: string }>
          | undefined;
        const audioUrl = payload['audio_url'] as string | undefined;
        const audioAzure = payload['audio_url_azure'] as string | undefined;
        const audioElevenlabs = payload['audio_url_elevenlabs'] as string | undefined;
        const createdAt = (payload['created_at'] as string) || new Date().toISOString();
        const tempId = currentTempId;

        if (currentEchoId) completedEchoIds.push(currentEchoId);
        const hasMoreEchoes = completedEchoIds.length < respondingEchoIds.length;

        if (tempId) {
          const finalBuffer = audioChunks.length > 0 ? [...audioChunks] : undefined;
          const nextEchoId = hasMoreEchoes
            ? (respondingEchoIds.find((id) => !completedEchoIds.includes(id)) ?? null)
            : null;
          const nextPlaceholder: GroupMessage | null = hasMoreEchoes
            ? {
                id: placeholderId,
                content: '',
                sender_type: MessageSender.AI,
                created_at: new Date().toISOString(),
                echo_id: nextEchoId,
                isTextComplete: false,
                isAudioReady: false,
              }
            : null;

          setActiveMessages((prev) => {
            const updated: GroupMessage[] = prev.map((msg) => {
              if (msg.id !== tempId) return msg;
              const result: GroupMessage = {
                ...msg,
                id: msgId || msg.id,
                content: finalContent,
                created_at: createdAt,
                isAudioReady: !!(finalBuffer ?? msg.audioBuffer)?.length || !!audioUrl,
                isTextComplete: true,
                isAllAudioStreamed: true,
                is_read: false,
              };
              if (suggestions) result.suggested_questions = suggestions;
              if (audioUrl) result.audio_url = audioUrl;
              if (audioAzure) result.audio_url_azure = audioAzure;
              if (audioElevenlabs) result.audio_url_elevenlabs = audioElevenlabs;
              const resolvedBuffer = finalBuffer ?? msg.audioBuffer;
              if (resolvedBuffer) result.audioBuffer = resolvedBuffer;
              return result;
            });
            return nextPlaceholder ? [...updated, nextPlaceholder] : updated;
          });
        }

        setTypingEchoName(null);
        currentTempId = null;
        currentEchoId = null;
        accumulatedText = '';
        audioChunks.length = 0;
        refreshProfile();
      };

      const handleStreamError = (payload: Record<string, unknown>) => {
        const errorMsg =
          (payload['message'] as string) ||
          (payload['detail'] as string) ||
          (payload['error'] as string) ||
          'An error occurred';
        setError(errorMsg);
        setTypingEchoName(null);
        setActiveMessages((prev) => prev.filter((msg) => msg.id !== placeholderId));
        if (
          payload['code'] === 'insufficient_credits' ||
          payload['error'] === 'Insufficient credits'
        ) {
          openBillingRecovery(billingRecoveryContext(payload, generateAudio ? 'VOICE' : 'TEXT'));
        }
      };

      const handleEvent = (evt: SSEEvent) => {
        const eventData = evt.data as Record<string, unknown>;
        switch (evt.event) {
          case 'group_start':
            handleGroupStart(eventData);
            break;
          case 'echo_start':
            handleEchoStart(eventData);
            break;
          case 'text_chunk':
            handleTextChunk(eventData);
            break;
          case 'audio_chunk': {
            const chunk = eventData['chunk'] as string;
            if (chunk) audioChunks.push(chunk);
            break;
          }
          case 'all_audio_complete':
            handleAllAudioComplete();
            break;
          case 'audio_error':
            handleAudioError();
            break;
          case 'echo_end':
            handleEchoEnd(eventData);
            break;
          case 'group_complete':
            setTypingEchoName(null);
            setActiveMessages((prev) => prev.filter((msg) => msg.id !== placeholderId));
            break;
          case 'error':
            handleStreamError(eventData);
            break;
          case 'keepalive':
            break;
          default:
            break;
        }
      };

      await groupChatService.streamChat(
        groupId,
        content,
        ttsProvider,
        handleEvent,
        controller.signal,
        generateAudio
      );
    },
    [groupId, ttsProvider, generateAudio, openBillingRecovery, refreshProfile]
  );

  const sendMessage = React.useCallback(
    async (content: string) => {
      if (!content.trim() || isSending) return;

      setIsSending(true);
      setError(null);

      const now = Date.now();
      const placeholderId = `temp-ai-waiting-${now}`;

      const userMsg: GroupMessage = {
        id: `temp-user-${now}`,
        content,
        sender_type: MessageSender.USER,
        created_at: new Date().toISOString(),
        echo_id: null,
        isTextComplete: true,
      };

      const waitingPlaceholder: GroupMessage = {
        id: placeholderId,
        content: '',
        sender_type: MessageSender.AI,
        created_at: new Date().toISOString(),
        echo_id: null,
        isTextComplete: false,
        isAudioReady: false,
      };

      setActiveMessages((prev) => [...prev, userMsg, waitingPlaceholder]);

      try {
        await _streamResponse(content, placeholderId);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        const errorMsg = (err as Record<string, string>)['message'] || 'Failed to send message';
        setError(errorMsg);
        addToast({ title: 'Send Failed', subtitle: errorMsg, type: 'error' });
        setActiveMessages((prev) => prev.filter((existing) => existing.id !== placeholderId));
      } finally {
        setIsSending(false);
        setTypingEchoName(null);
      }
    },
    [isSending, addToast, _streamResponse]
  );

  const sendAudioMessage = React.useCallback(
    async (blob: Blob, waveform: number[], duration: number) => {
      if (isSending) return;

      setIsSending(true);
      setError(null);

      const now = Date.now();
      const placeholderId = `temp-ai-waiting-${now}`;
      const localUrl = URL.createObjectURL(blob);

      // Optimistic user audio bubble
      const userAudioMsg: GroupMessage = {
        id: `temp-audio-${now}`,
        content: '',
        sender_type: MessageSender.USER,
        created_at: new Date().toISOString(),
        echo_id: null,
        isTextComplete: true,
        is_audio_message: true,
        localAudioUrl: localUrl,
        waveform,
        duration,
      };

      // AI waiting placeholder
      const waitingPlaceholder: GroupMessage = {
        id: placeholderId,
        content: '',
        sender_type: MessageSender.AI,
        created_at: new Date().toISOString(),
        echo_id: null,
        isTextComplete: false,
        isAudioReady: false,
      };

      setActiveMessages((prev) => [...prev, userAudioMsg, waitingPlaceholder]);

      try {
        // 1. Transcribe audio
        const result = await groupChatService.transcribeAudio(groupId, blob);

        if (!result.transcribed_text) {
          URL.revokeObjectURL(localUrl);
          addToast({
            title: 'Transcription Failed',
            subtitle: 'Could not understand the audio. Please try again.',
            type: 'error',
          });
          setActiveMessages((prev) =>
            prev.filter((msg) => msg.id !== userAudioMsg.id && msg.id !== placeholderId)
          );
          return;
        }

        // Update the user bubble with transcribed text
        setActiveMessages((prev) =>
          prev.map((msg) =>
            msg.id === userAudioMsg.id
              ? {
                  ...msg,
                  content: result.transcribed_text,
                  transcribed_text: result.transcribed_text,
                }
              : msg
          )
        );

        // 2. Stream AI response using transcribed text
        await _streamResponse(result.transcribed_text, placeholderId);
      } catch (err) {
        URL.revokeObjectURL(localUrl);
        if ((err as Error).name === 'AbortError') return;
        const errorMsg =
          (err as Record<string, string>)['message'] || 'Failed to send audio message';
        setError(errorMsg);
        addToast({ title: 'Send Failed', subtitle: errorMsg, type: 'error' });
        setActiveMessages((prev) => prev.filter((existing) => existing.id !== placeholderId));
      } finally {
        setIsSending(false);
        setTypingEchoName(null);
      }
    },
    [groupId, isSending, addToast, _streamResponse]
  );

  const clearConversation = React.useCallback(async () => {
    try {
      await groupChatService.clearHistory(groupId);
      setActiveMessages([]);
      // Reset history cache to empty
      queryClient.setQueryData(queryKey, () => ({
        pages: [],
        pageParams: [],
      }));
    } catch (err) {
      const errorMsg = (err as Record<string, string>)['message'] || 'Failed to clear conversation';
      addToast({ title: 'Error', subtitle: errorMsg, type: 'error' });
    }
  }, [groupId, queryClient, queryKey, addToast]);

  const loadMore = React.useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    messages,
    isLoading,
    error,
    hasMore: !!hasNextPage,
    total,
    sendMessage,
    sendAudioMessage,
    clearConversation,
    loadMore,
    isSending,
    typingEchoName,
  };
}
